import {
  Spinner,
  Box,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Icon
} from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import {
  useParams,
  useHistory
} from "react-router-dom";
import {
  addSeconds,
  format,
  formatDistance,
  formatDuration,
  isAfter,
  isBefore
} from "date-fns";
import {
  FaHeadphones,
  FaMicrophone
} from "react-icons/fa";

import { Header } from "src/components/shared/Header";
import { Text } from "src/components/text";
import {
  DestructiveButton,
  SolidButton
} from "src/components/button";
import { useConversationsApi } from "src/api/conversations/conversations.api";
import {
  Participant,
  Conversation
} from "src/api/conversations/conversations";
import { useAuthContext } from "src/hooks/useAuthContext";

interface PageParams {
  conversationId: string;
}

export const ConversationPage: React.FC = () => {
  const { conversationId } = useParams<PageParams>();
  const history = useHistory();
  const toast = useToast();
  const { authDataState } = useAuthContext();
  const [ conversationRes, getRequest ] = useConversationsApi("GET_CONVERSATION");
  const [ updateResponse, updateRequest ] = useConversationsApi("UPDATE_CONVERSATION");
  const [ removeResponse, removeRequest ] = useConversationsApi("DELETE_CONVERSATION");
  const [ participants, setParticipants ] = useState<Participant[]>([]);
  const [ creator, setCreator ] = useState<Participant>();
  const [ conversation, setConversation ] = useState<Conversation>();
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState(false);
  const [ microphonePermissionGranted, setMicrophonePermissionGranted ] = useState(false);
  const [ timeToStart, setTimeToStart ] = useState("");
  const [ serverTime, setServerTime ] = useState<Date | null>();
  const cancelRef = useRef();

  useEffect(() => {
    getRequest({ id: conversationId });
  }, [ getRequest, conversationId ]);

  useEffect(() => {
    if (conversationRes.data) {
      setCreator(conversationRes.data.participants?.find(p => p.creator));
      setParticipants(conversationRes.data.participants?.filter(p => !p.creator) || []);
      setConversation(conversationRes.data);
    }
  }, [ conversationRes ]);

  useEffect(() => {
    if (updateResponse.data) {
      toast({
        status: "success",
        title: "Conversation updated"
      });
      setCreator(updateResponse.data.participants?.find(p => p.creator));
      setParticipants(updateResponse.data.participants?.filter(p => !p.creator) || []);
      setConversation(updateResponse.data);
    }
  }, [ updateResponse, toast ]);

  const handleAcceptInvite = useCallback(() => {
    if (conversation?._id) {
      updateRequest({
        _id: conversation?._id,
        accept: true
      });
    }
  }, [ conversation, updateRequest ]);

  const handleRemoveConversation = useCallback(() => {
    if (conversation?._id) {
      removeRequest({ id: conversation?._id });
    }
  }, [ conversation, removeRequest ]);

  useEffect(() => {
    if (removeResponse.data) {
      history.push("/conversation/history");
    }

    if (removeResponse.error) {
      toast({
        status: "error",
        title: "Could not remove session."
      });
    }
  }, [
    removeResponse,
    toast,
    history
  ]);

  useEffect(() => {
    // Ask permission to use microphone:
    navigator.mediaDevices.getUserMedia({ audio: true }).then(() => {
      setMicrophonePermissionGranted(true);
    });
  }, []);

  useEffect(() => {
    const checkSetTime = () => {
      if (conversationRes.data && participants && conversationRes.serverTime && serverTime) {
        const conversation = conversationRes.data;
        const startTime = new Date(conversation.startTime);
        const endTime = new Date(conversation.startTime);
        const distanceToStart = formatDistance(startTime, serverTime, { includeSeconds: true });
        const isOver = isAfter(endTime, serverTime);
        const hasStarted = isAfter(serverTime, startTime);
        const accepted = participants[ 0 ]?.accepted;

        if (accepted && hasStarted && !isOver) {
          history.push(`/conversation/${conversation._id}/topic/${conversation.topics?.[ 0 ]._id}`);
        }

        if (!hasStarted) {
          const timeDiffInSeconds = Math.floor((startTime.getTime() - serverTime.getTime()) / 1000);
          const oneDayInSeconds = 24 * 60 * 60;

          if (timeDiffInSeconds < oneDayInSeconds) {
            const hours = Math.floor(timeDiffInSeconds / 3600);
            const minutes = Math.floor((timeDiffInSeconds % 3600) / 60);
            const seconds = timeDiffInSeconds % 60;

            setTimeToStart(`The session will start in ${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`);
          } else {
            setTimeToStart(`The session will start in ${distanceToStart}.`);
          }
        }
      }
    };

    // Run once on mount
    checkSetTime();
  }, [
    conversationRes.data,
    conversationRes.serverTime,
    history,
    participants,
    serverTime
  ]);

  useEffect(() => {
    // Run every second
    const interval = setInterval(() => {
      if (conversationRes.serverTime) {
        const serverTime = new Date(conversationRes.serverTime);

        setServerTime(currentTime => currentTime ? addSeconds(currentTime, 1) : serverTime);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [ conversationRes.serverTime ]);

  return (
    <Box p={10}>
      <Header />

      {(!conversation || !creator) ? (
        <Box>
          <Spinner />
        </Box>
      ) : (
        <Box paddingY={10}>
          <Box paddingY={10}>

            {creator?.user._id === authDataState.user?._id ? (
              <Box paddingTop="2rem">
                {participants.length && (
                  <>
                    <Text as="h1">
                      {`You have invited ${participants[ 0 ].user.name} to a session.`}
                    </Text>

                    <Box paddingTop="1rem">
                      <Text as="p">
                        {participants[ 0 ].accepted ? `This invitation has been accepted. ${timeToStart}` : "This invitation has not yet been accepted."}
                      </Text>
                    </Box>
                  </>
                )}
              </Box>
            ) : (
              <Box paddingTop="2rem">
                {participants[ 0 ].accepted ? (
                  <Box>
                    <Text as="h1">
                      {`You've accepted a session invitation from ${creator?.user.name}.`}
                    </Text>

                    <Box paddingTop="1rem">
                      <Text as="p">
                        {timeToStart}
                      </Text>
                    </Box>
                  </Box>
                ) : (

                  <Box paddingTop="2rem">
                    <Text as="h1">
                      {`${creator?.user.name} has invited you to a session`}
                    </Text>

                    <SolidButton
                      marginTop={5}
                      onClick={handleAcceptInvite}
                    >
                      Accept Invite
                    </SolidButton>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box paddingBottom="0.5rem">
            <Text
              as="h4"
              bold
            >
              Session details
            </Text>
          </Box>

          <Text
            as="p"
            bold
          >
            {conversation.mode === "voice" ? "Voice Mode (LGV)" : "Message Mode (LGM)"}

          </Text>

          <Text as="p">
            {format(new Date(conversation.date), "EEEE do MMMM")}
          </Text>

          <Text as="p">
            {`Start time: ${format(new Date(conversation.startTime), "HH:mm")}`}
          </Text>

          <Text as="p">
            {`End time: ${format(new Date(conversation.endTime), "HH:mm")}`}
          </Text>

          <Text as="p">
            The latency is
            {" "}

            <strong>
              {conversation.latencySeconds === 0 ? "0 seconds" : formatDuration({
                minutes: Math.floor(conversation.latencySeconds / 60),
                seconds: conversation.latencySeconds - Math.floor(conversation.latencySeconds / 60) * 60
              })}
            </strong>
            .
          </Text>

          {conversation.mode === "voice" && (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              paddingTop={10}
            >
              <Icon
                as={FaHeadphones}
                w={5}
                h={5}
                mr="0.5rem"
              />

              <Text as="p">
                For the best experience, please use headphones.
              </Text>
            </Box>
          )}

          {(conversation.mode === "voice" && !microphonePermissionGranted) && (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              paddingTop={4}
            >
              <Icon
                as={FaMicrophone}
                w={5}
                h={5}
                mr="0.5rem"
              />

              <Text as="p">
                Please ensure you have granted permission to use the microphone
              </Text>
            </Box>
          )}

          {!participants[ 0 ].accepted && creator?.user._id === authDataState.user?._id && (
            <Box
              display="flex"
              flexDirection="column"
            >

              {isBefore(new Date(), new Date(conversation.startTime)) && (
                <Box>
                  <SolidButton
                    marginTop={5}
                    onClick={() => history.push(`/conversation/${conversation._id}/edit`)}
                  >
                    Edit this session
                  </SolidButton>
                </Box>
              )}

              {isBefore(new Date(), new Date(conversation.endTime)) && (
                <Box>
                  <SolidButton
                    marginTop={5}
                    onClick={() => setIsDeleteModalOpen(true)}
                  >
                    Cancel this session
                  </SolidButton>

                  <AlertDialog
                    isOpen={isDeleteModalOpen}
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore- typings not exported properly from Chakra
                    leastDestructiveRef={cancelRef}
                    onClose={() => setIsDeleteModalOpen(false)}
                  >
                    <AlertDialogOverlay>
                      <AlertDialogContent>
                        <AlertDialogHeader
                          fontSize="lg"
                          fontWeight="bold"
                        >
                          Delete Conversation
                        </AlertDialogHeader>

                        <AlertDialogBody>
                          Are you sure? You can't undo this action afterwards.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                          <SolidButton
                            ref={cancelRef}
                            onClick={() => setIsDeleteModalOpen(false)}
                          >
                            No
                          </SolidButton>

                          <DestructiveButton
                            onClick={handleRemoveConversation}
                            ml={3}
                          >
                            Yes
                          </DestructiveButton>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialogOverlay>
                  </AlertDialog>
                </Box>
              )}

            </Box>
          )}

        </Box>
      )}
    </Box>
  );
};
